import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { authService, isDemoMode } from "./services/serviceFactory";
import Dashboard from "./pages/Dashboard";
import SignIn from "./pages/SignIn";
import ProductInfo from "./pages/ProductInfo";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import { Toaster } from "./components/ui/toaster";
import { ThemeProvider } from "./contexts/ThemeContext";
import "./App.css"; // Make sure to create this file if it doesn't exist
import { useAuthErrorNotification } from "./hooks/useAuthErrorNotification";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [userName, setUserName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [timeoutOccurred, setTimeoutOccurred] = useState<boolean>(false);

  // Enable authentication error notifications
  useAuthErrorNotification();

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // Set a timeout to detect if the request is hanging (only for real API)
        let timeoutId: NodeJS.Timeout | null = null;
        if (!isDemoMode()) {
          timeoutId = setTimeout(() => {
            setTimeoutOccurred(true);
            setLoading(false);
            setIsAuthenticated(false);
          }, 10000); // 10 second timeout
        }

        const response = await authService.getAuthStatus();
        if (timeoutId) clearTimeout(timeoutId);
        setIsAuthenticated(response.isAuthenticated);
        setIsAuthorized(response.isAuthorized);
        setUserName(response.userName || "");

        // Check for stored redirect path after successful authentication
        if (response.isAuthenticated && response.isAuthorized) {
          const redirectPath = localStorage.getItem("redirectAfterAuth");
          if (redirectPath) {
            localStorage.removeItem("redirectAfterAuth");
            // Use setTimeout to ensure state updates are complete
            setTimeout(() => {
              window.location.href = redirectPath;
            }, 100);
          }
        }
      } catch (error) {
        console.error("Error checking auth status:", error);
        setApiError(error instanceof Error ? error.message : String(error));
        setIsAuthenticated(false);
        setIsAuthorized(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Gate only auth-dependent routes on the API check. Public pages
  // (/info, /privacy, /terms) render immediately so crawlers and
  // visitors see content even if the backend is slow or down.
  const withAuthGate = (element: React.ReactNode): React.ReactNode => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
          <div className="p-4 text-lg font-medium text-ivy-green">
            Loading...
          </div>
          {timeoutOccurred && (
            <div className="mt-2 text-sm text-red-600 dark:text-red-400">
              Taking longer than expected. The API might be unresponsive.
            </div>
          )}
        </div>
      );
    }

    if (apiError && !isDemoMode()) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
          <div className="p-4 text-lg font-medium text-red-600 dark:text-red-400">
            Error connecting to API
          </div>
          <div className="p-2 mt-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded">
            {apiError}
          </div>
          <div className="mt-4">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 text-white bg-green-500 rounded hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    return element;
  };

  return (
    <ThemeProvider>
      <Router>
        <div className="app-container">
          <div className="app-content">
            <Routes>
              {/* Marketing page renders immediately (no auth gate) so
                  crawlers always see content; signed-in users are
                  redirected to the dashboard once the auth check resolves
                  (the Microsoft OAuth callback lands here and relies on it). */}
              <Route
                path="/"
                element={
                  isAuthenticated && isAuthorized ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <ProductInfo />
                  )
                }
              />
              <Route
                path="/dashboard/*"
                element={withAuthGate(
                  isAuthenticated && isAuthorized ? (
                    <Dashboard userName={userName} />
                  ) : (
                    <Navigate to="/signin" replace />
                  )
                )}
              />
              <Route path="/info" element={<Navigate to="/" replace />} />
              <Route
                path="/signin"
                element={withAuthGate(
                  isAuthenticated && isAuthorized ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <SignIn />
                  )
                )}
              />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
          <Toaster />
        </div>
      </Router>
      <Analytics />
      <SpeedInsights />
    </ThemeProvider>
  );
}

export default App;
